import { recordLastAction } from './app-context';
import { reloadOnChunkError } from './chunk-reload';
import { exceptionFieldsFromValue, maskPii } from './exception';
// index 와 순환 import 지만 logger 는 이벤트 시점에만 역참조하므로 안전 (ESM live binding)
import { logger } from './index';

/**
 * 전역 에러 캐치 3종 등록 (클라이언트 전용, 1회):
 * 1. window 'error' — 동기 에러
 * 2. window 'unhandledrejection' — Promise 거부 (비-Error reason 분기 포함)
 * 3. document 'click' — 마지막 사용자 액션 1건 기록 (app.last_action)
 */

let registered = false;

function describeClickTarget(target: EventTarget | null): string {
  if (!(target instanceof Element)) {
    return 'click:unknown';
  }
  const dataAttr =
    firstDataAttribute(target) ??
    firstDataAttribute(target.closest('button, a, [role="button"]'));
  if (dataAttr) {
    return `click:${dataAttr}`;
  }
  const text = target.textContent?.trim().slice(0, 30);
  return `click:${text || target.tagName.toLowerCase()}`;
}

function firstDataAttribute(el: Element | null): string | undefined {
  if (!el) {
    return undefined;
  }
  const attr = Array.from(el.attributes).find(({ name }) =>
    name.startsWith('data-'),
  );
  return attr ? `${attr.name}=${attr.value.slice(0, 30)}` : undefined;
}

export function registerGlobalErrorHandlers(): void {
  if (registered || typeof window === 'undefined') {
    return;
  }
  registered = true;

  // 리소스 로드 에러는 bubble 되지 않으므로 non-capture 로 스크립트 에러만 수신
  window.addEventListener('error', (event) => {
    if (event.error instanceof Error) {
      logger.error({ err: event.error }, maskPii(event.error.message));
    } else {
      const fields = exceptionFieldsFromValue(event.message, 'Error');
      logger.error(fields, fields.exception.message);
    }
    // 배포 skew 청크 404 는 에러 로그만 남기지 말고 새 HTML 로 1회 복구
    reloadOnChunkError(event.error ?? event.message);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const { reason } = event;
    if (reason instanceof Error) {
      logger.error({ err: reason }, maskPii(reason.message));
    } else {
      const fields = exceptionFieldsFromValue(reason, 'UnhandledRejection');
      logger.error(fields, fields.exception.message);
    }
    reloadOnChunkError(reason);
  });

  document.addEventListener(
    'click',
    (event) => {
      recordLastAction(describeClickTarget(event.target));
    },
    true,
  );
}
